import React, { useEffect, useState } from 'react';
import { Activity, RefreshCw, XCircle, CheckCircle2, Clock } from 'lucide-react';
import { Header } from '../components/Header.js';
import { EduCard } from '../components/EduCard.js';
import { EduButton } from '../components/EduButton.js';
import { EduBadge } from '../components/EduBadge.js';
import { EduProgressBar } from '../components/EduProgressBar.js';
import { api } from '../services/api.js';
import { RenderJob } from '../types/index.js';

export const Processings: React.FC = () => {
  const [jobs, setJobs] = useState<RenderJob[]>([]);
  const [loading, setLoading] = useState(true);

  const loadJobs = async () => {
    try {
      const res = await api.getJobs();
      setJobs(res.items || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadJobs();
    // Poll every 4 seconds to observe worker progress in real time
    const interval = setInterval(loadJobs, 4000);
    return () => clearInterval(interval);
  }, []);

  const handleCancel = async (jobId: string) => {
    if (!confirm('Deseja cancelar o processamento deste job? A franquia reservada será devolvida.')) return;
    try {
      await api.cancelJob(jobId);
      loadJobs();
    } catch (err: any) {
      alert(err.message || 'Falha ao cancelar processamento.');
    }
  };

  const getStatusBadge = (status: RenderJob['status']) => {
    switch (status) {
      case 'completed':
        return <EduBadge variant="success" dot>Concluído</EduBadge>;
      case 'failed':
        return <EduBadge variant="error" dot>Falhou</EduBadge>;
      case 'cancelled':
        return <EduBadge variant="neutral" dot>Cancelado</EduBadge>;
      case 'queued':
        return <EduBadge variant="warning" dot>Na Fila</EduBadge>;
      default:
        return <EduBadge variant="orange" dot>Processando</EduBadge>;
    }
  };

  return (
    <div>
      <Header
        title="Processamentos"
        subtitle="Acompanhe o status da fila, síntese de áudio Piper e renderização FFmpeg em tempo real"
        action={
          <EduButton
            variant="secondary"
            size="sm"
            onClick={loadJobs}
            icon={<RefreshCw size={14} className={loading ? 'animate-spin' : ''} />}
          >
            Atualizar
          </EduButton>
        }
      />

      <div className="p-8 max-w-7xl mx-auto space-y-6">
        {loading && jobs.length === 0 ? (
          <div className="p-12 text-center text-xs text-txt-muted">Carregando tarefas...</div>
        ) : jobs.length === 0 ? (
          <EduCard>
            <div className="text-center py-12">
              <Activity size={36} className="mx-auto text-txt-muted mb-2" />
              <h3 className="text-sm font-semibold text-txt-primary">Nenhum processamento recente</h3>
              <p className="text-xs text-txt-secondary mt-1">
                Ao solicitar a renderização de uma aula, você poderá acompanhar o progresso passo a passo aqui.
              </p>
            </div>
          </EduCard>
        ) : (
          <div className="space-y-4">
            {jobs.map((job) => (
              <EduCard key={job.id} className="border-l-4 border-l-brand-primary">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xs font-bold text-txt-secondary">
                        Job #{job.id.substring(0, 8)}
                      </span>
                      {getStatusBadge(job.status)}
                    </div>
                    <h3 className="text-base font-bold text-txt-primary mt-1">
                      Aula ID: {job.lessonId}
                    </h3>
                    <p className="text-xs text-txt-secondary mt-0.5">
                      Etapa atual: <strong className="text-txt-primary">{job.currentStep || 'Aguardando worker'}</strong>
                    </p>
                  </div>

                  <div className="flex items-center gap-3">
                    {['queued', 'validating', 'downloading', 'generating_audio', 'rendering_scenes', 'assembling_video'].includes(
                      job.status
                    ) && (
                      <EduButton
                        variant="secondary"
                        size="sm"
                        onClick={() => handleCancel(job.id)}
                        icon={<XCircle size={14} className="text-status-error" />}
                      >
                        Cancelar
                      </EduButton>
                    )}
                  </div>
                </div>

                {/* Progress Bar */}
                <div className="mb-4">
                  <EduProgressBar
                    progress={job.progress}
                    label={`Cena ${job.currentScene || 0} de ${job.totalScenes || 1}`}
                    status={job.status === 'failed' ? 'error' : job.status === 'completed' ? 'success' : 'normal'}
                  />
                </div>

                {/* Logs / Details Footer */}
                {job.errorMessage ? (
                  <div className="p-3 bg-red-50 border border-red-200 text-status-error text-xs rounded-lg mt-2">
                    <strong>Motivo da falha:</strong> {job.errorMessage}
                  </div>
                ) : job.logs && job.logs.length > 0 ? (
                  <div className="p-3 bg-surface-secondary border border-bdr rounded-lg text-[11px] font-mono text-txt-secondary">
                    {job.logs[job.logs.length - 1]}
                  </div>
                ) : null}
              </EduCard>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
